import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { api, Channel, MediaItem, MediaKindFilter, Streamer, SyncState } from "../api/client";
import { MEDIA_PAGE_SIZE, normalizeKind, useMediaPages } from "../api/media";
import { LIST_PAGE_SIZE, useDebounced, usePagedList, useSyncStatuses } from "../api/paged";
import { KindTabs, MediaBrowser } from "../components/MediaBrowser";
import { SortSelect, DEFAULT_SORT, normalizeSort } from "../components/SortSelect";
import { ChevronLeftIcon, ChevronRightIcon, RefreshIcon, SearchIcon, TopicsIcon, TrashIcon } from "../components/icons";
import { SelectToggle, SelectionBar, useMediaSelection } from "../components/SelectionBar";
import { SlideshowButton } from "../components/Slideshow";
import { AlertStrip, BackBar, EmptyState, LoadingState, MoreFooter, PageHeader, Toggle } from "../components/ui";

interface ChannelResp { channel: Channel }
interface StreamersResp { streamers: Streamer[]; has_more?: boolean; total?: number }
interface TopicsResp { topics: Channel[]; has_more?: boolean; total?: number }

export function ChannelDetail() {
  const { id } = useParams();
  const qc = useQueryClient();

  const channelQ = useQuery<ChannelResp>({
    queryKey: ["channel", id],
    queryFn: () => api.get(`/api/channels/${id}`),
    enabled: !!id,
  });
  const channel = channelQ.data?.channel;
  const isForum = !!channel?.is_forum;
  const isTopic = channel?.dialog_kind === "topic";
  const grouped = !!channel?.group_by_streamer;

  // The view state lives in the URL, not in component state, so that coming
  // back from the player (or a reload) lands on the same filtered list. With
  // useState it all reset on every navigation.
  //
  // ?streamer absent = streamer-list view; present (possibly "") = that
  // streamer's videos.
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.has("streamer") ? searchParams.get("streamer") ?? "" : null;
  const setSelected = (s: string | null) => {
    const p = new URLSearchParams(searchParams);
    if (s === null) p.delete("streamer");
    else p.set("streamer", s);
    setSearchParams(p);
  };

  const toggleGroup = useMutation({
    mutationFn: (val: boolean) =>
      api.patch<ChannelResp>(`/api/channels/${id}`, { group_by_streamer: val }),
    onSuccess: (resp) => {
      qc.setQueryData(["channel", id], resp);
      setSelected(null);
    },
    onError: (e: Error) => alert(`切换失败: ${e.message}`),
  });

  const counts = [
    (channel?.video_count ?? 0) > 0 && `${(channel?.video_count ?? 0).toLocaleString()} 个视频`,
    (channel?.photo_count ?? 0) > 0 && `${(channel?.photo_count ?? 0).toLocaleString()} 张图片`,
    isForum && `${(channel?.topic_count ?? 0).toLocaleString()} 个话题`,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-5 p-4 md:p-6">
      {isTopic && channel?.parent_channel_id && (
        <BackBar to={`/channels/${channel.parent_channel_id}`}>返回话题列表</BackBar>
      )}

      <PageHeader
        title={channel?.title ?? "频道"}
        meta={
          <>
            {counts.length > 0 ? counts.join(" · ") : "暂无内容"}
            {channel?.username && ` · @${channel.username}`}
            {isTopic && " · 话题"}
          </>
        }
        actions={
          !isForum && (
            <Toggle
              label="按主播分组"
              title="按文件名里的主播名把视频分组展示"
              checked={grouped}
              disabled={!channel || toggleGroup.isPending}
              onChange={(v) => toggleGroup.mutate(v)}
            />
          )
        }
      />

      {isForum && <TopicList id={id!} channel={channel} />}
      {!isForum && !grouped && <MediaView id={id!} channel={channel} />}
      {!isForum && grouped && selected === null && <StreamerList id={id!} onPick={setSelected} />}
      {!isForum && grouped && selected !== null && (
        <StreamerVideos id={id!} streamer={selected} onBack={() => setSelected(null)} />
      )}
    </div>
  );
}

// Last topic-list search per group id, for this session (see TopicList).
const topicFilters = new Map<string, string>();

// TopicList is what a forum group shows instead of a media list: its content
// all lives one level down, in the topics.
//
// Each topic syncs independently — a group can hold dozens of topics with
// hundreds of thousands of messages each, so "sync the whole group" is the
// batch option, not the only one. Live progress for every row comes from the
// topics endpoint itself (one poll, not one per row).
function TopicList({ id, channel }: { id: string; channel?: Channel }) {
  const qc = useQueryClient();
  // Remembered per group for the session: coming back from a topic must show
  // the same filtered list, or the restored scroll offset lands on other rows.
  const [filter, setFilterState] = useState(() => topicFilters.get(id) ?? "");
  const setFilter = (v: string) => {
    topicFilters.set(id, v);
    setFilterState(v);
  };
  // A forum group that was synced before it was recognised as one has media on
  // its OWN row (getHistory on a forum returns every topic flattened), which
  // the topic view would otherwise hide forever.
  const orphaned = (channel?.video_count ?? 0) + (channel?.photo_count ?? 0);
  const [showOrphaned, setShowOrphaned] = useState(false);

  const clearOrphaned = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/channels/${id}/videos`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`清空失败 (${res.status})`);
      return res.json() as Promise<{ deleted: number }>;
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["channel", id] });
      setShowOrphaned(false);
      alert(`已清空 ${resp.deleted} 条未分话题的内容`);
    },
    onError: (e: Error) => alert(e.message),
  });

  // Search goes to the server (debounced) and the list pages: a forum can have
  // thousands of topics, and rendering every one of them up front — then
  // re-fetching the lot every 2s while a sync ran — is what made this page crawl
  // on a phone.
  const debounced = useDebounced(filter.trim());
  const { query: q, items: list, total } = usePagedList<Channel, TopicsResp>({
    key: ["channel", id, "topics", debounced],
    path: `/api/channels/${id}/topics`,
    params: new URLSearchParams(debounced ? { q: debounced } : {}),
    pick: (r) => r.topics,
    keyOf: (t) => t.id,
  });

  // Live progress for the group and every loaded topic, in one polled request.
  const statusIds = useMemo(() => [Number(id), ...list.map((t) => t.id)], [id, list]);
  const { states, anyRunning } = useSyncStatuses(statusIds, () => {
    // A sync just ended somewhere on this page: its counts (and so its place in
    // the most-media-first order) changed.
    qc.invalidateQueries({ queryKey: ["channel", id, "topics"] });
    qc.invalidateQueries({ queryKey: ["channel", id], exact: true });
  });
  const groupSync = states[id];

  const refresh = useMutation({
    mutationFn: () => api.post<{ topics: number }>(`/api/channels/${id}/topics/refresh`),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["channel", id] });
      alert(`已拉取 ${resp.topics} 个话题`);
    },
    onError: (e: Error) => alert(`拉取话题失败: ${e.message}`),
  });

  // Group-level sync: refresh the topic list, then walk every topic in turn.
  const syncAll = useMutation({
    mutationFn: () => api.post<SyncState>(`/api/channels/${id}/sync`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sync-statuses"] }),
    onError: (e: Error) => alert(`同步启动失败: ${e.message}`),
  });

  if (q.isLoading && !debounced) return <LoadingState />;

  return (
    <div className="space-y-5">
      <div className="card flex flex-wrap items-center gap-3 p-4">
        <div className="relative min-w-[220px] flex-1">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
          <input
            className="field pl-11"
            placeholder="搜索话题…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <span className="badge badge-gray">{(total ?? list.length).toLocaleString()} 个话题</span>
        <button
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="btn btn-outline btn-sm"
          title="从 Telegram 重新枚举该群组的话题(只拉列表,不拉消息)"
        >
          <RefreshIcon className="size-4" />
          {refresh.isPending ? "拉取中…" : "刷新话题"}
        </button>
        <button
          onClick={() => syncAll.mutate()}
          disabled={syncAll.isPending || anyRunning}
          className="btn btn-primary btn-sm"
          title="刷新话题列表后,逐个话题同步消息历史 — 话题多时很慢,建议按需单个同步"
        >
          <RefreshIcon className="size-4" />
          同步全部话题
        </button>
        {groupSync?.running && (
          <div className="w-full text-theme-xs text-warning-600 dark:text-warning-400">
            {groupSync.phase && groupSync.phase !== "syncing" && `${groupSync.phase} · `}
            全部话题同步中:已遍历 {groupSync.walked} · 视频 {groupSync.videos} · 图片 {groupSync.photos}
          </div>
        )}
      </div>

      {orphaned > 0 && (
        <AlertStrip tone="warning" title={`有 ${orphaned.toLocaleString()} 条内容没有归属话题`}>
          <div className="space-y-2">
            <div>
              这些是把该群组当普通频道抓取时留下的 —— 论坛群组的 getHistory
              会把所有话题的消息拍平到群组本身,所以它们没有话题归属。按话题重新同步后,
              同样的内容会重新落到各自的话题里。
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowOrphaned((v) => !v)}
                className="btn btn-outline btn-sm"
              >
                {showOrphaned ? "收起" : "查看这些内容"}
              </button>
              <button
                onClick={() => {
                  if (confirm(`清空这 ${orphaned} 条未分话题的内容?\n\n它们的收藏会一并删除。各话题自己同步的内容不受影响。`)) {
                    clearOrphaned.mutate();
                  }
                }}
                disabled={clearOrphaned.isPending}
                className="btn btn-danger btn-sm"
              >
                <TrashIcon className="size-4" />
                {clearOrphaned.isPending ? "清空中…" : "清空"}
              </button>
            </div>
          </div>
        </AlertStrip>
      )}

      {showOrphaned && <MediaView id={id} channel={channel} />}

      {q.isLoading ? (
        <LoadingState label="搜索中…" />
      ) : list.length === 0 ? (
        debounced ? (
          <EmptyState title="没有匹配的话题" />
        ) : (
          <EmptyState
            title="还没有话题"
            hint="点「刷新话题」从 Telegram 拉取该群组的话题列表,然后对感兴趣的话题单独点「同步」把里面的图片和视频入库。"
          />
        )
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 3xl:grid-cols-3">
          {list.map((t) => (
            <TopicCard key={t.id} topic={t} sync={states[t.id]} groupId={id} />
          ))}
        </div>
      )}

      <MoreFooter
        hasNextPage={!!q.hasNextPage}
        isFetchingNextPage={q.isFetchingNextPage}
        fetchNextPage={q.fetchNextPage}
        doneLabel="已加载全部话题"
        loaded={list.length}
        pageSize={LIST_PAGE_SIZE}
      />
    </div>
  );
}

// TopicCard: the body navigates into the topic, the controls act on it in place.
function TopicCard({ topic, sync: st, groupId }: { topic: Channel; sync?: SyncState; groupId: string }) {
  const qc = useQueryClient();
  const running = !!st?.running;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["channel", groupId, "topics"] });
  };

  const sync = useMutation({
    mutationFn: () => api.post<SyncState>(`/api/channels/${topic.id}/sync`),
    // Start polling right away rather than waiting for the next list refresh.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sync-statuses"] }),
    onError: (e: Error) => alert(`同步启动失败: ${e.message}`),
  });
  const autoSync = useMutation({
    mutationFn: (val: boolean) => api.patch(`/api/channels/${topic.id}`, { auto_sync: val }),
    onSuccess: invalidate,
    onError: (e: Error) => alert(`切换自动同步失败: ${e.message}`),
  });

  const bits = [
    topic.video_count > 0 && `${topic.video_count.toLocaleString()} 视频`,
    topic.photo_count > 0 && `${topic.photo_count.toLocaleString()} 图片`,
  ].filter(Boolean) as string[];
  const err = st?.last_error;
  const note = st?.note;

  return (
    <div className="card flex flex-col gap-3 p-4">
      <div className="flex items-center gap-3">
        <Link to={`/channels/${topic.id}`} className="group flex min-w-0 flex-1 items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-400 transition-colors group-hover:bg-brand-500 group-hover:text-white dark:bg-white/[0.06] dark:text-gray-500">
            <TopicsIcon className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-gray-800 transition-colors group-hover:text-brand-600 dark:text-white/90 dark:group-hover:text-brand-400">
              {topic.title}
            </div>
            <div className="mt-0.5 truncate text-theme-xs text-gray-500 dark:text-gray-400">
              {bits.length > 0 ? bits.join(" · ") : "尚未同步"}
              {topic.topic_closed && " · 已关闭"}
            </div>
          </div>
          <ChevronRightIcon className="size-5 shrink-0 text-gray-300 transition-colors group-hover:text-brand-500 dark:text-gray-600" />
        </Link>
      </div>

      {running && (
        <div className="text-theme-xs text-warning-600 dark:text-warning-400">
          同步中:已遍历 {st?.walked ?? 0} · 视频 {st?.videos ?? 0} · 图片{" "}
          {st?.photos ?? 0} · 跳过 {st?.skipped ?? 0}
        </div>
      )}
      {!running && err && (
        <div className="truncate text-theme-xs text-error-600 dark:text-error-400" title={err}>
          上次同步失败: {err}
        </div>
      )}
      {!running && !err && note && (
        <div
          className="truncate text-theme-xs text-blue-light-600 dark:text-blue-light-400"
          title={note}
        >
          {note}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3 dark:border-gray-800">
        <button
          onClick={() => sync.mutate()}
          disabled={running || sync.isPending}
          className="btn btn-primary btn-sm"
          title="只同步这个话题的消息历史(增量 + 回填,可中断续传)"
        >
          <RefreshIcon className="size-4" />
          {running ? "同步中…" : "同步"}
        </button>
        <Toggle
          size="sm"
          label="自动同步"
          checked={topic.auto_sync}
          disabled={autoSync.isPending}
          onChange={(v) => autoSync.mutate(v)}
          title="是否纳入后台定时同步(手动同步不受此开关影响)"
        />
      </div>
    </div>
  );
}

// MediaView is the default channel/topic view: videos and images interleaved by
// date, with a kind filter, free-text search and sorting.
function MediaView({ id, channel }: { id: string; channel?: Channel }) {
  const qc = useQueryClient();
  // q / order / kind are read from the URL (see ChannelDetail); only the
  // half-typed search box is local.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const order = normalizeSort(searchParams.get("order"));
  const kind = normalizeKind(searchParams.get("kind"));
  const [draft, setDraft] = useState(query);
  useEffect(() => setDraft(query), [query]);

  // replace, not push: flipping a filter shouldn't add a Back step — Back
  // should leave the page, which is what the player's return link mirrors.
  const patch = (next: { q?: string; order?: string; kind?: MediaKindFilter }) => {
    const p = new URLSearchParams(searchParams);
    const set = (k: string, v: string | undefined, dflt = "") => {
      if (v === undefined) return;
      if (v && v !== dflt) p.set(k, v);
      else p.delete(k);
    };
    set("q", next.q);
    set("order", next.order, DEFAULT_SORT);
    set("kind", next.kind);
    setSearchParams(p, { replace: true });
  };
  const setQuery = (v: string) => patch({ q: v });
  const setOrder = (v: string) => patch({ order: v });
  const setKind = (v: MediaKindFilter) => patch({ kind: v });

  const sel = useMediaSelection();
  const { query: q, items, totalVideos, totalPhotos } = useMediaPages(
    ["channel", id, "media", query, order, kind],
    () => {
      const qs = new URLSearchParams();
      if (query) qs.set("q", query);
      if (order !== DEFAULT_SORT) qs.set("order", order);
      if (kind) qs.set("kind", kind);
      return qs;
    },
    { path: `/api/channels/${id}/media` },
  );

  // A topic (or channel) that has never been synced holds nothing yet — say so
  // and offer the one action that fixes it, instead of a bare "empty".
  const neverSynced = !!channel && !channel.last_indexed_at;
  const sync = useMutation({
    mutationFn: () => api.post<SyncState>(`/api/channels/${id}/sync`),
    onSuccess: () => {
      alert("已开始同步,进度可以在 TG 账号管理页或上级话题列表里看到。");
      qc.invalidateQueries({ queryKey: ["channel", id] });
    },
    onError: (e: Error) => alert(`同步启动失败: ${e.message}`),
  });

  const linkTo = (m: MediaItem) => {
    const params = new URLSearchParams();
    params.set("ch", String(id));
    if (query) params.set("q", query);
    if (order !== DEFAULT_SORT) params.set("order", order);
    if (kind) params.set("kind", kind);
    return `/videos/${m.id}?${params}`;
  };

  return (
    <div className="space-y-5">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(draft.trim());
        }}
        className="card flex flex-wrap items-center gap-3 p-4"
      >
        <div className="relative min-w-[220px] flex-1">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
          <input
            className="field pl-11"
            placeholder="搜索 file_name 或正文 (text) — 留空回到全部"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <button className="btn btn-primary">搜索</button>
        {(draft || query) && (
          <button
            type="button"
            onClick={() => {
              setDraft("");
              setQuery("");
            }}
            className="btn btn-outline"
          >
            清空
          </button>
        )}
        <SortSelect value={order} onChange={setOrder} className="field field-select w-auto" />
        <KindTabs
          value={kind}
          onChange={setKind}
          counts={{ videos: totalVideos, photos: totalPhotos }}
        />
        <SelectToggle sel={sel} />
        <SlideshowButton
          items={items}
          hasMore={!!q.hasNextPage}
          loadingMore={q.isFetchingNextPage}
          onLoadMore={q.fetchNextPage}
        />
      </form>

      <div className="text-theme-xs text-gray-500 dark:text-gray-400">
        {query ? (
          <>
            “{query}” 命中{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">{items.length}</span> 条
            {q.hasNextPage ? " (还有更多)" : ""}
          </>
        ) : (
          <>
            已加载{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">{items.length}</span> 条
          </>
        )}
      </div>

      {!q.isLoading && items.length === 0 && !query && neverSynced ? (
        <EmptyState
          title="这里还没有内容"
          hint="该话题/频道还没有同步过。点下面的按钮从 Telegram 拉取消息历史,视频和图片都会入库(增量 + 回填,中断可续传)。"
          action={
            <button
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
              className="btn btn-primary"
            >
              <RefreshIcon className="size-4" />
              {sync.isPending ? "启动中…" : "同步这个话题"}
            </button>
          }
        />
      ) : (
        <MediaBrowser
          items={items}
          isLoading={q.isLoading}
          linkTo={linkTo}
          hasMore={!!q.hasNextPage}
          loadingMore={q.isFetchingNextPage}
          onLoadMore={q.fetchNextPage}
          selection={sel.selection}
          onSelectMany={sel.selectMany}
          emptyLabel={query ? "无匹配结果" : "暂无内容"}
        />
      )}

      <MoreFooter
        hasNextPage={!!q.hasNextPage}
        isFetchingNextPage={q.isFetchingNextPage}
        fetchNextPage={q.fetchNextPage}
        doneLabel={query ? "已加载全部命中" : "已加载全部"}
        loaded={items.length}
        pageSize={MEDIA_PAGE_SIZE}
      />

      <SelectionBar sel={sel} items={items} />
    </div>
  );
}

// StreamerList is the top level of the grouped view: a grid of streamer cards.
// Grouping is a videos-only filename convention, so images are out of scope here.
function StreamerList({ id, onPick }: { id: string; onPick: (s: string) => void }) {
  const [filter, setFilter] = useState("");
  // Server-side search + paging: a channel following the
  // "{streamer}-DATE" convention can have thousands of streamers.
  const debounced = useDebounced(filter.trim());
  const { query: q, items: list, total } = usePagedList<Streamer, StreamersResp>({
    key: ["channel", id, "streamers", debounced],
    path: `/api/channels/${id}/streamers`,
    params: new URLSearchParams(debounced ? { q: debounced } : {}),
    pick: (r) => r.streamers,
    keyOf: (s) => s.streamer,
  });

  if (q.isLoading && !debounced) return <LoadingState />;
  if (!debounced && list.length === 0) return <EmptyState title="暂无视频" />;

  return (
    <div className="space-y-5">
      <div className="card flex flex-wrap items-center gap-3 p-4">
        <div className="relative min-w-[220px] flex-1">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
          <input
            className="field pl-11"
            placeholder="搜索主播…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <span className="badge badge-gray">{(total ?? list.length).toLocaleString()} 位主播</span>
      </div>

      {q.isLoading ? (
        <LoadingState label="搜索中…" />
      ) : list.length === 0 ? (
        <EmptyState title="没有匹配的主播" />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {list.map((s) => (
            <button
              key={s.streamer || "__other__"}
              onClick={() => onPick(s.streamer)}
              className="card group p-4 text-left transition-colors hover:border-brand-300 hover:bg-brand-25 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/[0.06]"
            >
              <div className="truncate font-medium text-gray-800 transition-colors group-hover:text-brand-600 dark:text-white/90 dark:group-hover:text-brand-400">
                {s.streamer || <span className="text-gray-400">其它 (未匹配)</span>}
              </div>
              <div className="mt-1 text-theme-xs text-gray-500 dark:text-gray-400">
                {s.count.toLocaleString()} 视频
              </div>
            </button>
          ))}
        </div>
      )}

      <MoreFooter
        hasNextPage={!!q.hasNextPage}
        isFetchingNextPage={q.isFetchingNextPage}
        fetchNextPage={q.fetchNextPage}
        doneLabel="已加载全部主播"
        loaded={list.length}
        pageSize={LIST_PAGE_SIZE}
      />
    </div>
  );
}

// StreamerVideos is the drill-down: one streamer's videos, paginated.
function StreamerVideos({ id, streamer, onBack }: { id: string; streamer: string; onBack: () => void }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const order = normalizeSort(searchParams.get("order"));
  const setOrder = (v: string) => {
    const p = new URLSearchParams(searchParams);
    if (v !== DEFAULT_SORT) p.set("order", v);
    else p.delete("order");
    setSearchParams(p, { replace: true });
  };

  const sel = useMediaSelection();
  const { query: q, items } = useMediaPages(
    ["channel", id, "streamer-media", streamer, order],
    () => {
      const qs = new URLSearchParams({ streamer });
      if (order !== DEFAULT_SORT) qs.set("order", order);
      return qs;
    },
    { path: `/api/channels/${id}/media` },
  );

  return (
    <div className="space-y-5">
      <div className="card flex flex-wrap items-center gap-3 p-4">
        <button onClick={onBack} className="btn btn-outline btn-sm">
          <ChevronLeftIcon className="size-4" />
          返回主播列表
        </button>
        <span className="truncate font-medium text-gray-800 dark:text-white/90">
          {streamer || "其它 (未匹配)"}
        </span>
        <span className="text-theme-xs text-gray-500 dark:text-gray-400">已加载 {items.length}</span>
        <SortSelect value={order} onChange={setOrder} className="field field-select ml-auto w-auto" />
        <SelectToggle sel={sel} className="btn-sm" />
        <SlideshowButton
          items={items}
          hasMore={!!q.hasNextPage}
          loadingMore={q.isFetchingNextPage}
          onLoadMore={q.fetchNextPage}
          className="btn-sm"
        />
      </div>

      <MediaBrowser
        items={items}
        isLoading={q.isLoading}
        selection={sel.selection}
        onSelectMany={sel.selectMany}
        emptyLabel="该主播暂无视频"
        linkTo={(m) => {
          const params = new URLSearchParams();
          params.set("ch", String(id));
          params.set("streamer", streamer);
          if (order !== DEFAULT_SORT) params.set("order", order);
          return `/videos/${m.id}?${params}`;
        }}
      />

      <MoreFooter
        hasNextPage={!!q.hasNextPage}
        isFetchingNextPage={q.isFetchingNextPage}
        fetchNextPage={q.fetchNextPage}
        doneLabel="已加载全部"
        loaded={items.length}
        pageSize={MEDIA_PAGE_SIZE}
      />

      <SelectionBar sel={sel} items={items} />
    </div>
  );
}
